import { Icon } from '@iconify/react';
import type { ReactNode } from 'react';

export interface EmptyProps {
  icon?: string;
  description?: string;
  title?: string;
  body?: string;
  actions?: ReactNode;
  /** Override the small decorative icon at bottom-left (when using title variant). Default: mdi:account-off-outline */
  bottomLeftIcon?: string;
  /** Override the small decorative icon at top-right (when using title variant). Default: mdi:comment-outline */
  topRightIcon?: string;
}

export function Empty({
  icon,
  description,
  title,
  body,
  actions,
  bottomLeftIcon = 'mdi:account-off-outline',
  topRightIcon = 'mdi:comment-outline',
}: EmptyProps) {
  if (!icon && !description && !title && !body) {
    return null;
  }

  if (title) {
    return (
      <div className='flex flex-col items-center gap-6 px-6 py-12'>
        {icon && (
          <div className='relative flex h-40 w-40 items-center justify-center'>
            <div className='absolute inset-0 rounded-full border border-indigo-100/80 bg-gradient-to-br from-indigo-50 via-slate-50 to-blue-50' />
            <div className='relative flex h-28 w-28 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md'>
              <Icon
                icon={icon}
                width='52'
                height='52'
                className='text-blue-600'
              />
            </div>
            <div className='absolute right-0 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200/80 bg-white shadow-md'>
              <Icon
                icon={topRightIcon}
                width='16'
                height='16'
                className='text-slate-500'
              />
            </div>
            <div className='absolute bottom-4 left-0 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200/80 bg-white shadow-md'>
              <Icon
                icon={bottomLeftIcon}
                width='16'
                height='16'
                className='text-slate-400'
              />
            </div>
          </div>
        )}
        <div className='flex max-w-xs flex-col items-center gap-3 text-center'>
          <h3 className='text-xl font-bold text-slate-900'>{title}</h3>
          {body && (
            <p className='text-sm leading-relaxed text-slate-500'>{body}</p>
          )}
        </div>
        {actions && <div className='flex gap-3'>{actions}</div>}
      </div>
    );
  }

  return (
    <div className='m-8 flex flex-col items-center gap-1'>
      {icon && (
        <Icon className='text-default-400' icon={icon} width='48' height='48' />
      )}
      {description && (
        <div className='font-medium text-default-400'>{description}</div>
      )}
    </div>
  );
}
